<template>
                <div v-if="currentView === 'dashboard'">
                    <div class="page-header">
                        <div>
                            <div class="page-title">系統狀態儀表板</div>
                            <div class="page-subtitle">即時監控郵件流量、服務健康與安全威脅</div>
                        </div>
                        <div class="page-actions">
                            <button class="btn btn-secondary btn-sm" @click="if(isSuperAdmin || isDomainAdmin) { fetchSpamStats(); fetchServiceStatus(); } if(isSuperAdmin) fetchAlerts()">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                                重新整理
                            </button>
                        </div>
                    </div>

                    <!-- Stat overview row -->
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div class="stat-card">
                            <div class="stat-icon stat-icon-blue">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                            </div>
                            <div>
                                <div class="stat-value">{{ domains.length }}</div>
                                <div class="stat-label">網域數</div>
                            </div>
                        </div>
                        <div class="stat-card">
                            <div class="stat-icon stat-icon-indigo">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                            </div>
                            <div>
                                <div class="stat-value">{{ users.length }}</div>
                                <div class="stat-label">使用者數</div>
                            </div>
                        </div>
                        <div class="stat-card">
                            <div class="stat-icon stat-icon-red">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            </div>
                            <div>
                                <div class="stat-value">{{ spamStats.total || 0 }}</div>
                                <div class="stat-label">垃圾攔截數</div>
                            </div>
                        </div>
                        <div class="stat-card">
                            <div class="stat-icon stat-icon-orange">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                            </div>
                            <div>
                                <div class="stat-value">{{ spamStats.avg_score || 0 }}</div>
                                <div class="stat-label">平均垃圾分</div>
                            </div>
                        </div>
                    </div>

                    <!-- Alert Summary Banner (super admin only, shown when there are danger/warning alerts) -->
                    <div v-if="isSuperAdmin && alertSummary && (alertSummary.danger > 0 || alertSummary.warning > 0)"
                        class="mb-5 card p-4 flex flex-wrap items-center gap-3"
                        :style="alertSummary.danger > 0 ? 'border-left:4px solid var(--color-danger);' : 'border-left:4px solid var(--color-warning);'">
                        <div style="display:flex;align-items:center;gap:8px;flex:1;">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                :style="alertSummary.danger > 0 ? 'color:var(--color-danger)' : 'color:var(--color-warning)'">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                            <div>
                                <span v-if="alertSummary.danger > 0" class="font-bold" style="color:var(--color-danger);">{{ alertSummary.danger }} 個危急告警</span>
                                <span v-if="alertSummary.danger > 0 && alertSummary.warning > 0"> · </span>
                                <span v-if="alertSummary.warning > 0" style="color:var(--color-warning);">{{ alertSummary.warning }} 個警告</span>
                                <span class="ml-2 text-sm" style="color:var(--gray-500);">請前往告警中心查看詳情</span>
                            </div>
                        </div>
                        <button @click="switchSidebarView('alert_center')" class="btn btn-sm"
                            :style="alertSummary.danger > 0 ? 'background:var(--color-danger);color:#fff;border-color:var(--color-danger);' : 'background:var(--color-warning);color:#fff;border-color:var(--color-warning);'">
                            前往告警中心
                        </button>
                    </div>

                    <!-- Charts Row -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                        <!-- Traffic Chart -->
                        <div class="card">
                            <div class="card-header">
                                <div class="card-title">郵件流量統計</div>
                                <span class="badge badge-default">近 7 日</span>
                            </div>
                            <div class="card-body">
                                <div style="height:220px;position:relative;">
                                    <canvas id="trafficChart"></canvas>
                                </div>
                            </div>
                        </div>

                        <!-- Spam Stats -->
                        <div class="card">
                            <div class="card-header">
                                <div class="card-title">垃圾郵件攔截</div>
                                <span class="badge badge-default">近 30 日</span>
                            </div>
                            <div class="card-body">
                                <div v-if="spamStats && spamStats.rspamd_error" class="mb-3 text-xs" style="color:var(--color-danger)">
                                    Rspamd 不可用：{{ spamStats.rspamd_error }}
                                </div>
                                <div class="flex items-center gap-4">
                                    <div style="width:160px;height:160px;position:relative;flex-shrink:0;">
                                        <canvas id="spamChart"></canvas>
                                    </div>
                                    <div>
                                        <div class="mb-4">
                                            <div class="stat-label">總攔截數</div>
                                            <div class="stat-value" style="color:var(--color-danger);">{{ spamStats.total || 0 }}</div>
                                        </div>
                                        <div>
                                            <div class="stat-label">平均垃圾分</div>
                                            <div class="stat-value" style="color:var(--color-warning);">{{ spamStats.avg_score || 0 }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <!-- Service Status -->
                        <div class="card">
                            <div class="card-header">
                                <div class="card-title">服務健康狀態</div>
                            </div>
                            <div class="card-body" style="padding-top:0.75rem;">
                                <div class="space-y-2">
                                    <div v-for="(status, service) in serviceStatus" :key="service"
                                        class="flex justify-between items-center" style="padding:0.5rem 0;border-bottom:1px solid var(--border);">
                                        <div class="flex items-center gap-2">
                                            <span class="status-dot" :class="status === 'running' ? 'online' : 'error'"></span>
                                            <span class="capitalize" style="font-size:0.875rem;font-weight:500;color:var(--gray-700)">{{ service }}</span>
                                        </div>
                                        <span class="badge" :class="status === 'running' ? 'badge-success' : 'badge-danger'">{{ status }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Real-time Logs -->
                        <div class="col-span-2" style="background:#0d1117;border:1px solid rgba(255,255,255,0.08);border-radius:var(--radius-xl);font-family:ui-monospace,'Cascadia Code',Consolas,monospace;font-size:0.8rem;color:#4ade80;display:flex;flex-direction:column;height:320px;">
                            <div style="display:flex;justify-content:space-between;align-items:center;padding:0.875rem 1rem;border-bottom:1px solid rgba(255,255,255,0.08);">
                                <div style="display:flex;gap:0.75rem;align-items:center;">
                                    <div style="display:flex;align-items:center;gap:0.5rem;">
                                        <span class="status-dot online"></span>
                                        <span style="color:rgba(255,255,255,0.8);font-weight:700;font-family:inherit;">Live Logs</span>
                                    </div>
                                    <select v-model="logService"
                                        :disabled="!isSuperAdmin"
                                        style="background:#1a2332;border:1px solid rgba(255,255,255,0.12);border-radius:6px;padding:3px 8px;font-size:0.75rem;color:rgba(255,255,255,0.75);cursor:pointer;">
                                        <option value="postfix">Postfix</option>
                                        <option value="dovecot">Dovecot</option>
                                        <option value="rspamd">Rspamd</option>
                                        <option value="ai_service">AI Service</option>
                                    </select>
                                </div>
                                <button @click="toggleLogStream" class="btn btn-xs"
                                    :disabled="!isSuperAdmin"
                                    :style="logWs ? 'background:#7f1d1d;color:#fca5a5;border-color:#991b1b;' : 'background:#064e3b;color:#6ee7b7;border-color:#065f46;'">
                                    {{ logWs ? '■ 停止' : '▶ 開始' }}
                                </button>
                                <button @click="clearRealtimeLogs" class="btn btn-xs"
                                    :disabled="!realtimeLogs.length"
                                    style="background:#1e293b;color:rgba(255,255,255,0.5);border-color:rgba(255,255,255,0.15);">
                                    🗑 清空
                                </button>
                            </div>
                            <div ref="logContainer"
                                style="flex:1;overflow-y:auto;padding:0.75rem;white-space:pre-wrap;line-height:1.6;">
                                <div v-if="realtimeLogs.length === 0" style="color:rgba(255,255,255,0.3);font-style:italic;text-align:center;margin-top:2.5rem;">
                                    點擊「開始監控」以檢視實時日誌...</div>
                                <div v-for="(line, i) in realtimeLogs" :key="i" style="padding:1px 2px;border-radius:2px;" onmouseover="this.style.background='rgba(255,255,255,0.05)'" onmouseout="this.style.background=''">{{ line }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- RAG Chat View -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Dashboard live logs
  realtimeLogs, logWs, logService, isSuperAdmin, isDomainAdmin, alertSummary,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  toggleLogStream, clearRealtimeLogs, fetchAlerts,
} = store
</script>

